import { Injectable, signal } from '@angular/core';
import { OperatorFunction, tap } from 'rxjs';

export function setLoaderCounter<T>(
  loader: LoaderService
): OperatorFunction<T, T> {
  return tap({
    next: () => loader.decrementLoader(),
    error: () => loader.decrementLoader(),
  });
}

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  #loaderCounter = signal(0);
  #loaderContexts = signal<Map<string, number>>(new Map());

  isLoading = (context?: string): boolean => {
    if (!context) {
      // Global: true if any context is loading OR legacy counter > 0
      const contextsLoading = Array.from(this.#loaderContexts().values()).some(
        (count) => count > 0
      );
      return contextsLoading || this.#loaderCounter() > 0;
    }

    // Check exact match first
    if (this.#loaderContexts().get(context) ?? 0 > 0) {
      return true;
    }

    // If no exact match, check if any URL contains the pattern
    return Array.from(this.#loaderContexts().keys()).some(
      (url) =>
        url.includes(context) && (this.#loaderContexts().get(url) ?? 0) > 0
    );
  };

  isLoadingAny(contexts: string[]): boolean {
    return contexts.some((context) => this.isLoading(context));
  }

  incrementLoader(context?: string): void {
    if (context) {
      this.#loaderContexts.update((map) => {
        const newMap = new Map(map);
        newMap.set(context, (newMap.get(context) ?? 0) + 1);
        return newMap;
      });
    } else {
      // Legacy behavior: increment global counter
      this.#loaderCounter.update((count) => count + 1);
    }
  }

  decrementLoader(context?: string): void {
    if (context) {
      this.#loaderContexts.update((map) => {
        const newMap = new Map(map);
        const count = Math.max(0, (newMap.get(context) ?? 0) - 1);
        if (count === 0) {
          newMap.delete(context);
        } else {
          newMap.set(context, count);
        }
        return newMap;
      });
    } else {
      // Legacy behavior: decrement global counter
      this.#loaderCounter.update((count) => Math.max(0, count - 1));
    }
  }
}
